import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import instance from "api/globalaxios";
import React, { useState } from "react";
import { v4 as uuid } from "uuid";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    maxWidth: 700,
    margin: "0 auto",
    textAlign: "left",
  },
  helperText: {
    fontSize: "0.875rem",
    color: TEXT.muted,
    marginTop: theme.spacing(1),
  },
  generateBtn: {
    padding: theme.spacing(1.25, 4),
    fontSize: "1rem",
    textTransform: "none",
    borderRadius: 6,
    fontWeight: 600,
  },
  resultCard: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    borderRadius: 8,
    backgroundColor: "#FAFAFA",
    // Inside the page card's own padding, 24px a side left the link little
    // room on a phone.
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  resultTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
    color: TEXT.strong,
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: `${theme.spacing(1.5)}px`,
    marginTop: theme.spacing(2),
  },
  // Both the same size, so the pair reads as one set of actions.
  actionBtn: {
    padding: theme.spacing(0.75, 2),
    fontSize: "0.875rem",
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  copiedText: {
    fontSize: "0.8rem",
    color: TEXT.success,
    fontWeight: 600,
  },
  errorText: {
    color: TEXT.error,
    fontSize: "0.875rem",
  },
}));

/**
 * Generates a standalone video-conference link that can be shared with
 * anyone (parties, counsels, witnesses) without them needing a case login.
 */
export default function GenerateVCLink({ disputeId: propDisputeId }) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  async function generateLink() {
    setLoading(true);
    setError("");
    setMeetingUrl("");
    setIsCopied(false);

    try {
      const response = await instance.post("/creategooglemeeting/", {
        dispute_id: propDisputeId || uuid(),
      });

      const url = response?.data?.data?.meeting_url;
      if (url) {
        setMeetingUrl(url);
      } else {
        setError("Could not generate the link. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.reason ||
          "Could not generate the link. Please try again."
      );
    }
    setLoading(false);
  }

  function copyLink() {
    if (meetingUrl) {
      navigator.clipboard.writeText(meetingUrl);
      setIsCopied(true);
    }
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.helperText}>
        Create an instant video conference link and share it with the
        participants.
      </Typography>

      <Box height="2rem" />

      <Button
        variant="contained"
        color="primary"
        className={classes.generateBtn}
        onClick={generateLink}
        disabled={loading}
      >
        {loading && (
          <CircularProgress
            style={{
              width: 15,
              height: 15,
              marginRight: "0.5rem",
              color: TEXT.onDark,
            }}
          />
        )}
        Generate VC Link
      </Button>

      {error && (
        <Box mt={2}>
          <Typography className={classes.errorText}>{error}</Typography>
        </Box>
      )}

      {meetingUrl && (
        <Paper className={classes.resultCard}>
          <Typography className={classes.resultTitle}>Meeting Link</Typography>
          {/* The link on its own full-width line, the actions centred below. */}
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={meetingUrl}
            InputProps={{
              readOnly: true,
            }}
          />
          <Box className={classes.actions}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.actionBtn}
              startIcon={<FileCopyOutlinedIcon fontSize="small" />}
              onClick={copyLink}
            >
              Copy Link
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionBtn}
              onClick={() => window.open(meetingUrl, "_blank")}
            >
              Open Meeting
            </Button>
          </Box>
          {isCopied && (
            <Box mt={1} textAlign="center">
              <Typography className={classes.copiedText}>
                Link copied to clipboard
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}


